export interface TProductUse {
  product: ProductName;
  sales: TProductSales[];
  patients: TProductPatients[];
}

export interface TProductSales {
  time_period: string;
  sales_count: number;
}

export interface TProductPatients {
  time_period: string;
  patients_count: number;
}

export interface TProductSalesTableData {
  product: ProductName;
  time_period: string;
  sales_count: number;
  patients_count: number;
}

interface HasTimePeriod {
  time_period: string;
}

export type ProductName = string;
export type FormularyStatus = 1 | 2 | 3;

// TODO: move to utils, test it
export function validateTimePeriodImportFormat(timePeriod: string): boolean {
  const pattern1 = /^M(0[1-9]|1[0-2])\d{4}$/;
  return pattern1.test(timePeriod);
}

export function validateTimePeriodSaveFormat(timePeriod: string): boolean {
  const pattern = /^M(0[1-9]|1[0-2])\d{4}$/;
  return pattern.test(timePeriod);
}

export function convertToTableData(productUses: TProductUse[]): TProductSalesTableData[] {
  return productUses.flatMap((productUse) => {
    return productUse.sales.map((sale) => {
      const p = productUse.patients?.find((p) => p.time_period === sale.time_period);
      return {
        product: productUse.product,
        time_period: sale.time_period,
        sales_count: sale.sales_count,
        patients_count: p ? p.patients_count : 0
      };
    });
  });
}

function sortedProductData<T extends HasTimePeriod>(data: T[]): T[] {
  function parseTimePeriod(timePeriod: string): {
    year: number;
    month: number;
  } {
    // "M012024" -> 'M' at index 0
    // month = substring(1, 3) => "01"
    // year  = substring(3)   => "2024"
    const month = parseInt(timePeriod.substring(1, 3), 10);
    const year = parseInt(timePeriod.substring(3), 10);
    return { year, month };
  }

  // Sort the data from earliest to latest
  return data.sort((a, b) => {
    const aParsed = parseTimePeriod(a.time_period);
    const bParsed = parseTimePeriod(b.time_period);

    // First compare by year
    if (aParsed.year !== bParsed.year) {
      return aParsed.year - bParsed.year;
    }

    // If same year, compare by month
    return aParsed.month - bParsed.month;
  });
}

export const sortedSales = (data: TProductSales[]) => sortedProductData(data);
export const sortedPatients = (data: TProductPatients[]) => sortedProductData(data);

function lastMonthValue<T extends { time_period: string }>(
  items: T[] | undefined,
  pick: (item: T) => number
): number {
  if (!items?.length) return 0;

  const sorted = sortedProductData(items);
  const latest = sorted[sorted.length - 1];
  return pick(latest);
}

export const salesLastMonth = (products: TProductUse[]): number =>
  lastMonthValue(products[0]?.sales, (s) => s.sales_count);

export const patientsLastMonth = (products: TProductUse[]): number =>
  lastMonthValue(products[0]?.patients, (p) => p.patients_count);
